import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle2, Clock, AlertCircle, Search, ListChecks, MessageSquare, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';

interface MyTask {
  userTaskId: number;
  taskId: number;
  title: string;
  description: string | null;
  taskType: 'aviso' | 'validacion' | 'respuesta_texto';
  moduleTarget: string | null;
  status: 'pendiente' | 'completada';
  completedAt: string | null;
  completionResponse: string | null;
  assignedAt: string;
  taskCreatedAt: string;
}

const TareasPendientes: React.FC = () => {
  const [tasks, setTasks] = useState<MyTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [completingId, setCompletingId] = useState<number | null>(null);
  const [responseDialogTask, setResponseDialogTask] = useState<MyTask | null>(null);
  const [responseText, setResponseText] = useState('');
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [creatingTask, setCreatingTask] = useState(false);
  const { toast } = useToast();

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const statusParam = filterStatus !== 'todas' ? `?status=${filterStatus}` : '';
      const res = await fetch(`/api/tasks/my/tasks${statusParam}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al obtener tareas');
      const data = await res.json();
      setTasks(data.tasks || []);
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudieron cargar las tareas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleComplete = async (userTaskId: number, completionResponse?: string) => {
    try {
      setCompletingId(userTaskId);
      const res = await fetch(`/api/tasks/my/tasks/${userTaskId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(completionResponse ? { completionResponse } : {}),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error al completar tarea');
      }
      toast({ title: 'Tarea completada', description: 'La tarea se marcó como completada' });
      setResponseDialogTask(null);
      setResponseText('');
      fetchTasks();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCompletingId(null);
    }
  };

  const handleResponseSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!responseDialogTask || !responseText.trim()) {
      toast({ title: 'Error', description: 'Debe ingresar una respuesta', variant: 'destructive' });
      return;
    }
    handleComplete(responseDialogTask.userTaskId, responseText.trim());
  };

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTaskTitle.trim()) {
      toast({ title: 'Error', description: 'El título es obligatorio', variant: 'destructive' });
      return;
    }
    try {
      setCreatingTask(true);
      const res = await fetch('/api/tasks/my/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: newTaskTitle.trim(),
          description: newTaskDescription.trim() || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Error al crear tarea');
      }
      toast({ title: 'Tarea creada', description: 'La tarea se agregó a tu lista' });
      setShowNewTaskDialog(false);
      setNewTaskTitle('');
      setNewTaskDescription('');
      fetchTasks();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setCreatingTask(false);
    }
  };

  const filteredTasks = tasks.filter((t) =>
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.description && t.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const pendingCount = tasks.filter(t => t.status === 'pendiente').length;
  const completedCount = tasks.filter(t => t.status === 'completada').length;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTaskTypeLabel = (type: string) => {
    if (type === 'aviso') return 'Aviso';
    if (type === 'respuesta_texto') return 'Respuesta de Texto';
    return 'Validación';
  };

  const getTaskTypeBadgeColor = (type: string) => {
    if (type === 'aviso') return 'bg-blue-100 text-blue-800';
    if (type === 'respuesta_texto') return 'bg-amber-100 text-amber-800';
    return 'bg-purple-100 text-purple-800';
  };

  const getStatusIcon = (status: string) => {
    return status === 'completada'
      ? <CheckCircle2 className="h-5 w-5 text-green-500" />
      : <Clock className="h-5 w-5 text-amber-500" />;
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ListChecks className="h-7 w-7 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Mis Tareas</h1>
        </div>
        <Button onClick={() => setShowNewTaskDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Tarea
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <ListChecks className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-xl font-bold text-gray-900">{tasks.length}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Clock className="h-5 w-5 text-amber-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Pendientes</p>
            <p className="text-xl font-bold text-amber-600">{pendingCount}</p>
          </div>
        </Card>
        <Card className="p-4 flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">Completadas</p>
            <p className="text-xl font-bold text-green-600">{completedCount}</p>
          </div>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar tareas..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterStatus === 'todas' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('todas')}
          >
            Todas
          </Button>
          <Button
            variant={filterStatus === 'pendiente' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('pendiente')}
          >
            <Clock className="h-4 w-4 mr-1" />
            Pendientes
          </Button>
          <Button
            variant={filterStatus === 'completada' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterStatus('completada')}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Completadas
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-500">Cargando tareas...</span>
        </div>
      ) : filteredTasks.length === 0 ? (
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-lg">No tienes tareas asignadas</p>
          <p className="text-gray-400 text-sm mt-1">
            {filterStatus !== 'todas'
              ? 'No hay tareas con el filtro seleccionado'
              : 'Cuando te asignen tareas aparecerán aquí'}
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => (
            <Card
              key={task.userTaskId}
              className={`p-4 transition-all hover:shadow-md ${
                task.status === 'completada' ? 'bg-gray-50 opacity-75' : 'bg-white'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="mt-1">
                  {getStatusIcon(task.status)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-semibold text-gray-900 ${task.status === 'completada' ? 'line-through' : ''}`}>
                      {task.title}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getTaskTypeBadgeColor(task.taskType)}`}>
                      {getTaskTypeLabel(task.taskType)}
                    </span>
                    {task.moduleTarget && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        {task.moduleTarget}
                      </span>
                    )}
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                  )}
                  {task.status === 'completada' && task.completionResponse && (
                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-xs text-green-700 font-medium mb-0.5">Respuesta registrada:</p>
                      <p className="text-sm text-green-800">{task.completionResponse}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span>Asignada: {formatDate(task.assignedAt)}</span>
                    {task.completedAt && (
                      <span>Completada: {formatDate(task.completedAt)}</span>
                    )}
                  </div>
                </div>
                {task.status === 'pendiente' && task.taskType === 'aviso' && (
                  <Button
                    size="sm"
                    onClick={() => handleComplete(task.userTaskId)}
                    disabled={completingId === task.userTaskId}
                    className="shrink-0"
                  >
                    {completingId === task.userTaskId ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Completar
                      </>
                    )}
                  </Button>
                )}
                {task.status === 'pendiente' && task.taskType === 'respuesta_texto' && (
                  <Button
                    size="sm"
                    variant="default"
                    onClick={() => {
                      setResponseDialogTask(task);
                      setResponseText('');
                    }}
                    disabled={completingId === task.userTaskId}
                    className="shrink-0 bg-amber-600 hover:bg-amber-700"
                  >
                    {completingId === task.userTaskId ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Responder
                      </>
                    )}
                  </Button>
                )}
                {task.status === 'completada' && (
                  <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-50 rounded shrink-0">
                    Completada
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showNewTaskDialog} onOpenChange={(open) => { if (!open) { setShowNewTaskDialog(false); setNewTaskTitle(''); setNewTaskDescription(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Tarea Personal</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-4">
            <div>
              <Label htmlFor="newTaskTitle">Título *</Label>
              <Input
                id="newTaskTitle"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="Ej: Revisar documentos, Llamar a proveedor..."
                required
              />
            </div>
            <div>
              <Label htmlFor="newTaskDesc">Descripción</Label>
              <Textarea
                id="newTaskDesc"
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                placeholder="Detalles adicionales de la tarea (opcional)"
                rows={3}
              />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowNewTaskDialog(false); setNewTaskTitle(''); setNewTaskDescription(''); }}>
                Cancelar
              </Button>
              <Button type="submit" disabled={creatingTask || !newTaskTitle.trim()}>
                {creatingTask ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Crear Tarea
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!responseDialogTask} onOpenChange={(open) => { if (!open) { setResponseDialogTask(null); setResponseText(''); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Responder Tarea</DialogTitle>
          </DialogHeader>
          {responseDialogTask && (
            <form onSubmit={handleResponseSubmit} className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-md">
                <p className="font-medium text-gray-900">{responseDialogTask.title}</p>
                {responseDialogTask.description && (
                  <p className="text-sm text-gray-600 mt-1">{responseDialogTask.description}</p>
                )}
              </div>
              <div>
                <Label htmlFor="responseText">Respuesta *</Label>
                <Textarea
                  id="responseText"
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  placeholder="Escribe tu respuesta aquí..."
                  rows={4}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ingresa el valor requerido para completar esta tarea
                </p>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => { setResponseDialogTask(null); setResponseText(''); }}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={completingId === responseDialogTask.userTaskId || !responseText.trim()}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {completingId === responseDialogTask.userTaskId ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Enviar y Completar
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TareasPendientes;
